import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getDramasByCategory, type Drama } from '@/mocks/dramas';
import DramaCard from '@/pages/home/components/DramaCard';

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const [dramas, setDramas] = useState<Drama[]>([]);
  const [visibleCount, setVisibleCount] = useState(12);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, searchQuery]);

  useEffect(() => {
    let filtered = getDramasByCategory(slug);
    if (searchQuery) {
      filtered = filtered.filter(
        (d) =>
          d.title.includes(searchQuery) ||
          d.tags.some((t) => t.includes(searchQuery)) ||
          d.cast.some((c) => c.includes(searchQuery)) ||
          d.description.includes(searchQuery),
      );
    }
    setDramas(filtered);
    setVisibleCount(12);
  }, [slug, searchQuery]);

  const visibleDramas = dramas.slice(0, visibleCount);

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 12, dramas.length));
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 久久影视`,
    description: `久久影视${currentCategory.name}分类，高清全集免费在线观看。`,
    url: typeof window !== 'undefined' ? window.location.href : '',
    isPartOf: {
      '@type': 'WebSite',
      name: '久久影视',
      url: typeof window !== 'undefined' ? window.location.origin : '',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-8 py-8">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">{currentCategory.name}</span>
              {searchQuery && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-600">搜索：{searchQuery}</span>
                </>
              )}
            </nav>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {searchQuery ? `搜索：${searchQuery}` : currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {searchQuery
                    ? `找到 ${dramas.length} 部相关剧集`
                    : `共 ${dramas.length} 部${currentCategory.name}`}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-300'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {visibleDramas.length > 0 ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
                  {visibleDramas.map((drama) => (
                    <DramaCard key={drama.slug} drama={drama} />
                  ))}
                </div>

                {visibleCount < dramas.length && (
                  <div className="flex justify-center mt-10">
                    <button
                      onClick={handleLoadMore}
                      className="px-8 py-2.5 rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium border border-background-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      加载更多 ({dramas.length - visibleCount} 部)
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-search-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500 text-sm mb-2">没有找到相关剧集</p>
                <p className="text-xs text-foreground-400">试试其他关键词或分类吧</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}